import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { contratacaoService } from '@/services/contratacaoService';
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  RefreshCcw,
  ArrowRight,
  Rocket,
  Settings,
  LayoutDashboard,
  Users,
  Building2,
  Shield,
  Zap,
  Globe,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { ContratacaoPublicShell } from '@/components/contratacao/ContratacaoPublicShell';
import {
  trackCheckoutSuccessPolling,
  trackCheckoutSuccessConfirmed,
  trackCheckoutSuccessFailed,
} from '@/lib/tracking';

const statusMessages: string[] = [
  'Preparando sua conta…',
  'Aplicando as configurações iniciais…',
  'Estruturando sua dashboard personalizada…',
  'Montando o site da sua imobiliária…',
  'Integrando módulos e funcionalidades…',
  'Validando informações e permissões…',
  'Ajustando tudo para o melhor desempenho…',
  'Ativando super velocidade 🚀',
  'Quase pronto! Finalizando os últimos detalhes…',
  'Estamos deixando tudo do seu jeito…',
  'Seu site está tomando forma…',
  'Organizando os bastidores da sua operação…',
  'Preparando um ambiente rápido e seguro…',
  'Ajustes finais para uma experiência completa…',
  'Preparando tudo para você…',
  'Ajustando as configurações iniciais…',
  'Montando sua dashboard personalizada…',
  'Organizando os detalhes da sua ImobDesk…',
  'Estruturando o ambiente ideal para sua operação…',
  'Conectando as funcionalidades…',
  'Deixando tudo pronto para uso…',
  'Conferindo se está tudo certo…',
  'Aplicando os ajustes finais…',
  'Ativando alto desempenho 🚀',
  'Quase pronto! Só mais um momento…',
];

/** Ícones embaralhados em paralelo às mensagens — mesmo índice = mesmo “momento” da narrativa. */
const statusIcons: LucideIcon[] = [
  Rocket,
  Settings,
  LayoutDashboard,
  Users,
  Building2,
  Shield,
  Zap,
  Globe,
  Sparkles,
];

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

type Status = 'processando' | 'pronto' | 'erro';

export default function Sucesso() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState<Status>('processando');
  const [mensagemErro, setMensagemErro] = useState<string>('');
  const [shuffledMessages] = useState(() => shuffleArray(statusMessages));
  const [shuffledIcons] = useState(() => shuffleArray(statusIcons));
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const tentativasRef = useRef(0);

  const maxTentativas = 20;
  const intervalo = 3000;

  useEffect(() => {
    if (status !== 'processando') return;

    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % shuffledMessages.length);
    }, 2500);

    return () => clearInterval(messageInterval);
  }, [status, shuffledMessages.length]);

  const fallbackMainAppUrl = useMemo(() => {
    const raw = import.meta.env.VITE_MAIN_APP_URL?.trim();
    if (!raw) return 'https://app.imobdesk.com.br';
    return raw.replace(/\/$/, '');
  }, []);

  const trustedOrigins = useMemo(() => {
    const origins = new Set<string>([window.location.origin]);
    const loginApp = import.meta.env.VITE_LOGIN_APP_URL?.trim();
    const mainApp = import.meta.env.VITE_MAIN_APP_URL?.trim();
    for (const candidate of [loginApp, mainApp]) {
      if (!candidate) continue;
      try {
        origins.add(new URL(candidate).origin);
      } catch {
        // Ignora env inválida e mantém lista padrão.
      }
    }
    return origins;
  }, []);

  const isValidRedirectUrl = useCallback(
    (url: string): boolean => {
      try {
        const parsed = new URL(url);
        if (!/^https?:$/i.test(parsed.protocol)) return false;
        if (trustedOrigins.has(parsed.origin)) return true;
        if (parsed.hostname === 'localhost' || parsed.hostname.endsWith('.localhost')) return true;
        return parsed.hostname === 'imobdesk.com.br' || parsed.hostname.endsWith('.imobdesk.com.br');
      } catch {
        return false;
      }
    },
    [trustedOrigins],
  );

  const verificarStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus('erro');
      setMensagemErro('Session ID não encontrado. Verifique o link de redirecionamento.');
      return;
    }

    try {
      const result = await contratacaoService.checkoutSuccess(sessionId);

      if (!result.processando && result.redirectUrl) {
        trackCheckoutSuccessConfirmed({ sessionId });
        if (isValidRedirectUrl(result.redirectUrl)) {
          setStatus('pronto');
          setTimeout(() => {
            window.location.href = result.redirectUrl!;
          }, 500);
          return true;
        }
        console.warn('Redirect URL inválida, usando URL padrão');
        setStatus('pronto');
        setTimeout(() => {
          window.location.href = fallbackMainAppUrl;
        }, 500);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Erro ao verificar status:', error);
      return false;
    }
  }, [sessionId, isValidRedirectUrl, fallbackMainAppUrl]);

  useEffect(() => {
    if (!sessionId) {
      setStatus('erro');
      setMensagemErro('Session ID não encontrado. Por favor, verifique o link.');
      trackCheckoutSuccessFailed({ reason: 'missing_session_id' });
      return;
    }

    trackCheckoutSuccessPolling({ sessionId });

    let intervalId: NodeJS.Timeout;

    const iniciarPolling = async () => {
      const pronto = await verificarStatus();
      if (pronto) return;

      intervalId = setInterval(async () => {
        const ok = await verificarStatus();

        if (ok) {
          clearInterval(intervalId);
          return;
        }

        tentativasRef.current += 1;
        if (tentativasRef.current >= maxTentativas) {
          clearInterval(intervalId);
          setStatus('erro');
          setMensagemErro(
            'O processamento está demorando mais que o esperado. Tente fazer login manualmente.',
          );
          trackCheckoutSuccessFailed({
            sessionId,
            reason: 'polling_timeout',
          });
        }
      }, intervalo);
    };

    void iniciarPolling();

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [sessionId, verificarStatus]);

  const currentMessage = shuffledMessages[currentMessageIndex];
  const CurrentIcon = shuffledIcons[currentMessageIndex % shuffledIcons.length];

  if (status === 'processando') {
    return (
      <ContratacaoPublicShell innerClassName="flex flex-col items-center text-center py-6 sm:py-8">
        <div className="relative mb-1 flex shrink-0 flex-col items-center">

                <div className="flex flex-col items-center pb-4">  <h1 className="h-step mt-16">Pagamento confirmado</h1>
        <p className="t-step mb-1">Estamos finalizando a ativação da sua conta.</p></div>
          <div className="relative flex h-24 w-24 items-center justify-center">
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden>
              <div className="h-24 w-24 rounded-full border-2 border-indigo-200/90 animate-ping" />
            </div>
            <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-indigo-100 animate-pulse">
              <CurrentIcon
                key={currentMessageIndex}
                className="h-12 w-12 text-indigo-600 transition-all duration-500"
                aria-hidden
              />
            </div>
          </div>
          <p
            key={currentMessageIndex}
            className="mt-5 max-w-md text-pretty text-lg font-semibold leading-snug text-indigo-600 transition-opacity duration-300"
          >
            {currentMessage}
          </p>
        </div>


        <p className="mt-4 text-sm text-slate-500">Aguarde alguns instantes…</p>
      </ContratacaoPublicShell>
    );
  }

  if (status === 'pronto') {
    return (
      <ContratacaoPublicShell innerClassName="flex flex-col items-center text-center py-6 sm:py-8">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-emerald-50">
          <CheckCircle2 className="h-9 w-9 text-emerald-600" aria-hidden />
        </div>
        <h1 className="h-step mt-6">Tudo pronto</h1>
        <p className="t-step mb-8">Sua conta foi ativada com sucesso. Você será redirecionado em instantes.</p>
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" aria-label="Redirecionando" />
      </ContratacaoPublicShell>
    );
  }

  return (
    <ContratacaoPublicShell innerClassName="flex flex-col items-center text-center py-6 sm:py-8">
      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-red-50">
        <AlertCircle className="h-9 w-9 text-red-600" aria-hidden />
      </div>
      <h1 className="h-step mt-6">Não conseguimos concluir agora</h1>
      <p className="t-step mb-8">{mensagemErro}</p>
      <div className="flex w-full max-w-sm flex-col gap-3">
        <button type="button" className="cta cta--indigo" onClick={() => window.location.reload()}>
          <RefreshCcw className="h-5 w-5" />
          Tentar novamente
        </button>
        <button
          type="button"
          className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-base font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
          onClick={() => {
            window.location.href = fallbackMainAppUrl;
          }}
        >
          Ir para o login
          <ArrowRight className="h-5 w-5 text-slate-500" />
        </button>
      </div>
    </ContratacaoPublicShell>
  );
}
